import {
  DateFormatter,
  getLocalTimeZone,
  parseAbsoluteToLocal,
} from "@internationalized/date"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import { formatZonedDateTimeWithTimeZone, getLocale } from "@/utilities/etc"
import { receiptQueryOptions } from "@/utilities/hooks/queries/shared"
import useToggle from "@/utilities/hooks/useToggle"
import {
  normalizedAPILicensePlate,
  prettyCurrencyNumber,
} from "@/utilities/normalizers"

import SendReceiptModal from "./-components/SendReceiptModal"

export const Route = createFileRoute("/receipt/$receiptID")({
  beforeLoad: ({ context }) => {
    context.queryClient.clear()
  },
  component: () => <RouteComponent />,
})

function RouteComponent() {
  const { receiptID } = Route.useParams()
  const { t } = useTranslation()
  const { data } = useSuspenseQuery(receiptQueryOptions({ receiptID }))
  const [sendPhoneReceiptModalOpen, togglePhoneSendReceiptModal] =
    useToggle(false)
  const [sendEmailReceiptModalOpen, toggleEmailSendReceiptModal] =
    useToggle(false)

  const dateFormatter = new DateFormatter(getLocale(), {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: getLocalTimeZone(),
  })

  const endTime = parseAbsoluteToLocal(data.receipt.endTime).toDate()

  return (
    <>
      <Heading level={3} as={1} className="center">
        {dateFormatter.format(endTime)}
      </Heading>
      {data.receipt.accessCode && (
        <Card>
          <Heading level={4} as={2} className="center">
            {t("receipt.accessCode.header")}
          </Heading>
          <Text>{t("receipt.accessCode.body")}</Text>
          <span>{data.receipt.accessCode}</span>
        </Card>
      )}
      <Heading level={4} as={2}>
        {t("receipt.sessionDetails.header")}
      </Heading>
      <dl>
        <div>
          <dt>{t("receipt.sessionDetails.list.location")}</dt>
          <dd>{data.receipt.locationName}</dd>
        </div>
        <div>
          <dt>{t("receipt.sessionDetails.list.date")}</dt>
          <dd>{dateFormatter.format(endTime)}</dd>
        </div>
      </dl>
      <Separator orientation="horizontal" />
      <Heading level={4} as={2}>
        {t("receipt.vehicle.header")}
      </Heading>
      <dl>
        <div>
          <dt>{t("receipt.vehicle.list.licensePlate")}</dt>
          <dd>{normalizedAPILicensePlate(data.receipt.plate)}</dd>
        </div>
      </dl>
      <Separator orientation="horizontal" />
      <Heading level={4} as={2}>
        {t("receipt.sessionInformation.header")}
      </Heading>
      <dl>
        <div>
          <dt>{t("receipt.sessionInformation.list.lot")}</dt>
          <dd>{data.receipt.lotName}</dd>
        </div>
        <div>
          <dt>{t("receipt.sessionInformation.list.startTime")}</dt>
          <dd>
            {formatZonedDateTimeWithTimeZone(
              parseAbsoluteToLocal(data.receipt.startTime)
            )}
          </dd>
        </div>
        <div>
          <dt>{t("receipt.sessionInformation.list.endTime")}</dt>
          <dd>
            {formatZonedDateTimeWithTimeZone(
              parseAbsoluteToLocal(data.receipt.endTime)
            )}
          </dd>
        </div>
      </dl>
      <Separator orientation="horizontal" />
      <Heading level={4} as={2}>
        {t("receipt.paymentDetails.header")}
      </Heading>
      <dl>
        <div>
          <dt>{t("receipt.paymentDetails.list.invoiceNumber")}</dt>
          <dd>{data.receipt.invoiceID}</dd>
        </div>
        <div>
          <dt>{t("receipt.paymentDetails.list.paymentMethod")}</dt>
          <dd>{data.receipt.paymentDescription}</dd>
        </div>
        {/* TODO add this back in once fixed
        <div>
          <dt>Sub Total:</dt>
          <dd>{prettyCurrencyNumber(data.receipt.subtotal)}</dd>
        </div>
        */}
        {!!data.receipt.discount && (
          <div style={{ color: "var(--success-10)" }}>
            <dt>{t("receipt.paymentDetails.list.discount")}</dt>
            <dd style={{ fontWeight: 500 }}>
              -{prettyCurrencyNumber(data.receipt.discount)}
            </dd>
          </div>
        )}
        {!!data.receipt.tax && (
          <div>
            <dt>{t("receipt.paymentDetails.list.tax")}</dt>
            <dd>{prettyCurrencyNumber(data.receipt.tax)}</dd>
          </div>
        )}
        <div>
          <dt>{t("receipt.paymentDetails.list.totalPaid")}</dt>
          <dd>{prettyCurrencyNumber(data.receipt.amountPaid)}</dd>
        </div>
      </dl>
      <Button
        hue="secondary"
        variant="default"
        onPress={togglePhoneSendReceiptModal}
      >
        <Icon name="speechBubble" slot="left" />{" "}
        {t("receipt.sendReceiptViaText")}
      </Button>
      <Button
        hue="secondary"
        variant="default"
        onPress={toggleEmailSendReceiptModal}
      >
        <Icon name="mail" slot="left" /> {t("receipt.sendReceiptViaEmail")}
      </Button>
      <SendReceiptModal
        isOpen={sendPhoneReceiptModalOpen}
        onOpenChange={togglePhoneSendReceiptModal}
        receiptID={receiptID}
        method="phone"
      />
      <SendReceiptModal
        isOpen={sendEmailReceiptModalOpen}
        onOpenChange={toggleEmailSendReceiptModal}
        receiptID={receiptID}
        method="email"
      />
    </>
  )
}
